import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import {
  setRefreshStatus, updateLeadPipelineStatus,
  bulkUpdateLeadsThunk, fetchLeads, updateLocalLead, updateSales,
  updateLeadList,
} from "../../../Slices/salesSlice";
import { useLeadListKey, useLeadListField } from "./useLeadList";
import socket from "./socket";
import './Frames.scss'
import { Empty, message, Modal, Select, Tooltip } from "antd";
import AddSales from "./AddSales";
import SaleDetails from "./SaleDetails";
import SalesFilters from "./SalesFilters";
import DeleteModal from "../../../components/modelpopup/DeleteModal";
import { CustomGridTable } from "../../../blueplanitUtils/CustomGridTable";
import { faPen, faReply as faReplySolid } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SalesLeaderboard from "./SalesLeaderboard";
import DuplicateLeadMerge from "./DuplicateLeadMerge";

const Sales = () => {

  const navigate = useNavigate();
  const { id } = useParams()
  const dispatch = useDispatch();
  const location = useLocation();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);

  // This window's own lead list — independent from any other Leads window.
  const { listKey } = useLeadListKey();
  const open = useLeadListField(listKey, "createOpen");
  const page = useLeadListField(listKey, "page");
  const tableData = useLeadListField(listKey, "rows");
  const pageCount = useLeadListField(listKey, "pageCount");
  const prevVisibility = useLeadListField(listKey, "filterPanelOpen");
  const salesCounts = useLeadListField(listKey, "counts");
  const replyNotifLeadIds = useSelector((state) =>
    new Set((state.leadReplyNotifs?.notifications || []).map(n => n.leadId))
  );

  const now = Date.now();
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const todayEnd   = new Date(); todayEnd.setHours(23, 59, 59, 999);



  const getRowClassName = (record) => {
    if (!record.followup || !record.followupDate) return "sales-clickable-row";
    const d = parseInt(record.followupDate);
    if (d < todayStart.getTime()) return "sales-clickable-row row-overdue";
    if (d <= todayEnd.getTime()) return "sales-clickable-row row-due-today";
    return "sales-clickable-row";
  };

  const columns = [
    {
      title: "S.No",
      dataIndex: "leadID",
      key: "leadID",
      ellipsis: { showTitle: false },
      width: 70,
      sorter: true,
    },
    {
      title: "Agency Name",
      dataIndex: "agencyName",
      key: "agencyName",
      width: 150,
      sorter: true,
      render: (text, record) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {text}
          {replyNotifLeadIds.has(record.id) && (
            <Tooltip title="New email reply received">
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                background: '#f5f3ff', color: '#7c3aed',
                border: '1px solid #c4b5fd', borderRadius: 10,
                padding: '1px 6px', fontSize: 10, fontWeight: 700, flexShrink: 0,
              }}>
                <FontAwesomeIcon icon={faReplySolid} style={{ fontSize: 8 }} />
                Reply
              </span>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      width: 80,
      sorter: true,
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      width: 80,
      sorter: true,
    },
    {
      title: "Stage",
      dataIndex: "stage",
      key: "stage",
      width: 80,
      sorter: true,
    },
    {
      title: "Follow-Up",
      dataIndex: "followupDate",
      key: "followupDate",
      width: 120,
      sorter: true,
      render: (text, record) => record.followup ? formatDateTime(text) : null,
    },
    {
      title: "Time Zone",
      dataIndex: "timeZone",
      key: "timeZone",
      width: 80,
      sorter: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 80,
      sorter: true,
      render: (status) => {
        if (!status) return null;
        const map = {
          "New":        { bg: "#dbeafe", text: "#1d4ed8" },
          "Active":     { bg: "#dcfce7", text: "#15803d" },
          "Nurturing":  { bg: "#fef9c3", text: "#854d0e" },
          "Closed":     { bg: "#f3f4f6", text: "#374151" },
          "Lost":       { bg: "#fee2e2", text: "#b91c1c" },
          "On Hold":    { bg: "#f3e8ff", text: "#6d28d9" },
        };
        const s = map[status] || { bg: "#f3f4f6", text: "#374151" };
        return (
          <span style={{
            background: s.bg, color: s.text,
            padding: "2px 7px", borderRadius: 10, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
          }}>
            {status}
          </span>
        );
      },
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 80,
      sorter: true,
      render: (priority) => {
        if (!priority) return null;
        const map = {
          "Low":    { bg: "#dcfce7", text: "#15803d" },
          "Medium": { bg: "#fef9c3", text: "#854d0e" },
          "High":   { bg: "#fee2e2", text: "#b91c1c" },
        };
        const s = map[priority] || { bg: "#f3f4f6", text: "#374151" };
        return (
          <span style={{
            background: s.bg, color: s.text,
            padding: "2px 7px", borderRadius: 10, fontSize: 11, fontWeight: 600,
          }}>
            {priority}
          </span>
        );
      },
    },
    {
      title: "Last Updated",
      dataIndex: "lastUpdatedDate",
      key: "lastUpdatedDate",
      width: 130,
      sorter: true,
      render: (text) => formatDateTime(text),
    },
    {
      title: "Last Contacted",
      dataIndex: "lastContactedDate",
      key: "lastContactedDate",
      width: 150,
      sorter: true,
      render: (text) => {
        if (!text) return "";
        const date = new Date(parseInt(text));
        const diffDays = Math.floor((Date.now() - date) / 86400000);
        const ageLabel = diffDays === 0 ? "today"
          : diffDays === 1 ? "1d ago"
          : `${diffDays}d ago`;
        const isOld = diffDays > 14;
        return (
          <Tooltip title={`${diffDays === 0 ? "Contacted today" : `Last contacted ${diffDays} day${diffDays !== 1 ? "s" : ""} ago`}`}>
            <span>
              {formatDateTime(text)}
              <span style={{
                marginLeft: 5, fontSize: 10, fontWeight: 600,
                color: isOld ? "#dc2626" : "#9ca3af",
              }}>
                · {ageLabel}
              </span>
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Channel",
      dataIndex: "channel",
      key: "channel",
      width: 80,
      sorter: true,
    },
    {
      title: "Assigned To",
      dataIndex: "assignedTo",
      key: "assignedTo",
      width: 120,
      sorter: true,
      render: (text, record) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {text}
          {record.isAssignLocked && (
            <Tooltip title="Locked">
              <span style={{ color: '#ef4444', fontSize: 11 }}>🔒</span>
            </Tooltip>
          )}
        </span>
      ),
    },

    {
      title: "Pipeline",
      key: "pipeline",
      width: 65,
      render: (_, record) => (
        <button
          onClick={(e) => { e.stopPropagation(); handleTogglePipeline(record); }}
          style={{
            padding: "2px 6px",
            fontSize: "12px",
            borderRadius: "6px",
            border: record.isInPipeline ? "1px solid green" : "1px solid #ccc",
            backgroundColor: record.isInPipeline ? "green" : "white",
            color: record.isInPipeline ? "white" : "black",
            cursor: "pointer",
          }}
        >
          {record.isInPipeline ? "In Pipeline" : "Add"}
        </button>
      ),
    },
    {
      title: "Pipeline Stage",
      dataIndex: "pipelineStage",
      key: "pipelineStage",
      width: 110,
      render: (stage, record) => {
        if (!stage || !record.isInPipeline) return null;
        const colors = {
          "Prospect":    { bg: "#e0f2fe", text: "#0369a1" },
          "Qualified":   { bg: "#fef9c3", text: "#854d0e" },
          "Proposal":    { bg: "#fde8d8", text: "#c2410c" },
          "Negotiation": { bg: "#ede9fe", text: "#6d28d9" },
          "Onboarding":  { bg: "#cffafe", text: "#0e7490" },
          "Closed Won":  { bg: "#dcfce7", text: "#15803d" },
          "Closed Lost": { bg: "#fee2e2", text: "#b91c1c" },
        };
        const style = colors[stage] || { bg: "#f3f4f6", text: "#374151" };
        return (
          <span style={{
            background: style.bg, color: style.text,
            padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600,
          }}>
            {stage}
          </span>
        );
      },
    },


    {
      title: "Edit",
      key: "edit",
      align: "center",
      width: 50,
      render: (text, record) => (
        <FontAwesomeIcon
          icon={faPen}
          onClick={(e) => { e.stopPropagation(); handleDetailPage(record?.id); }}
          style={{
            cursor: "pointer",
            fontSize: "14px",
            color: "#1677ff",
          }}
        />
      ),
    },
  ];

  useEffect(() => {
    dispatch(updateLeadList({ key: listKey, changes: { createOpen: false, filterPanelOpen: false } }));
    setSelectedRowKeys([]);
  }, [location.pathname]);

  // REMOVED (Rule 1 sync with Desktop fix): the detect-mismatch effect created a ping-pong
  // when two views shared state.sales.isPipeline. Pipeline now reads its own
  // state.sales.pipelineSales slot, so cross-tab corruption can't happen.

  // Real-time lead updates via Socket.IO — targeted row update, no full reload
  useEffect(() => {
    const handleLeadUpdated = (lead) => {
      if (lead?.id) dispatch(updateLocalLead({ leadId: lead.id, changes: lead }));
    };
    const handleLeadCreated = (lead) => {
      if (lead?.id) dispatch(updateSales(lead));
    };
    socket.on("lead:updated", handleLeadUpdated);
    socket.on("lead:created", handleLeadCreated);
    return () => {
      socket.off("lead:updated", handleLeadUpdated);
      socket.off("lead:created", handleLeadCreated);
    };
  }, [dispatch]);

  const handleDetailPage = (rowid) => {
    navigate(`/sales/${rowid}`, {
      state: { from: "sales" },
    });
  };

  const handleTogglePipeline = (lead) => {
    const newStatus = !lead.isInPipeline;

    const doUpdate = async () => {
      try {
        await dispatch(
          updateLeadPipelineStatus({ leadId: lead.id, isInPipeline: newStatus })
        ).unwrap();
        message.success(`Lead ${newStatus ? "added to" : "removed from"} pipeline`);
      } catch (err) {
        message.error("Failed to update pipeline status");
      }
    };

    if (!newStatus) {
      Modal.confirm({
        title: "Remove from Pipeline?",
        content: `"${lead.agencyName}" will be removed from the pipeline. This does not delete the lead.`,
        okText: "Remove",
        okButtonProps: { danger: true },
        cancelText: "Cancel",
        centered: true,
        onOk: doUpdate,
      });
    } else {
      doUpdate();
    }
  };

  function formatDateTime(timestamp) {
    if (timestamp !== null) {
      const date = new Date(parseInt(timestamp));
      const formattedDate = date.toLocaleString("en-IN", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });
      return formattedDate;
    } else {
      return "";
    }
  }


  const handleTableChange = (pagination, filters, sorter) => {
    dispatch(setRefreshStatus(null));
    // Update sortObject when the sorting changes
    const newSortObject = sorter?.order
      ? { [sorter.field]: sorter.order === "ascend" ? "asc" : "desc" }
      : {};
    dispatch(updateLeadList({
      key: listKey,
      changes: { sort: newSortObject, page: pagination.current },
    }));
  };

  const CustomNoRowsOverlay = () => (
    <div style={{ padding: "20px", textAlign: "center" }}>
      <Empty description="No rows found" />
    </div>
  );

  const handleClose = () => {
    dispatch(updateLeadList({ key: listKey, changes: { createOpen: false } }));
  };

  const leadFilters = useLeadListField(listKey, "leadFilters");

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    preserveSelectedRowKeys: true,
  };

  const handleBulkUpdate = useCallback(async (field, value) => {
    if (selectedRowKeys.length === 0) return;

    const variables = { ids: selectedRowKeys };

    // Handle assign fields together
    if (field === "assignedTo") {
      variables.assignedTo = value;
      variables.assignTo = value;
      variables.assign = true;
    } else {
      variables[field] = value;
    }

    setBulkLoading(true);
    try {
      const result = await dispatch(bulkUpdateLeadsThunk(variables)).unwrap();
      if (result.status === 200) {
        message.success(result.message);
        setSelectedRowKeys([]);
        // Refresh every open Leads/Pipeline window — the updated leads may be in any of them.
        dispatch(fetchLeads());
      } else {
        message.error(result.message);
      }
    } catch (err) {
      message.error("Bulk update failed");
    } finally {
      setBulkLoading(false);
    }
  }, [selectedRowKeys, dispatch]);

  const handleBulkPipeline = useCallback(async (addToPipeline) => {
    if (selectedRowKeys.length === 0) return;
    const action = addToPipeline ? "add to" : "remove from";

    Modal.confirm({
      title: `${addToPipeline ? "Add" : "Remove"} ${selectedRowKeys.length} lead(s) ${action} pipeline?`,
      okText: "Confirm",
      okButtonProps: { danger: !addToPipeline },
      centered: true,
      onOk: () => handleBulkUpdate("isInPipeline", addToPipeline),
    });
  }, [selectedRowKeys, handleBulkUpdate]);

  return (
    <>
      {id !== undefined ? (
        <SaleDetails
          handleClose={handleClose}
          SalesInfo={tableData.filter((item) => item?.id === id)[0]}
          isCreate={false}
        />
      ) : (
        <>
          {open ? (
            <div className="page-wrapper">
              <div className="content container-fluid">
                <AddSales
                  handleClose={handleClose}
                  isCreate={true}
                />
              </div>
            </div>
          ) : (
            <>
              <div className="page-wrapper">
                <div style={{ paddingLeft: '10px', paddingRight: '0px', marginRight: '5px' }} className="content container-fluid">
                  <SalesFilters
                    listRoute="/sales"
                  />
                  {/* KPI Summary Cards */}
                  <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                    {[
                      { label: "Total", value: salesCounts.total || pageCount || 0, bg: "#f0f9ff", border: "#bae6fd", text: "#0369a1" },
                      { label: "In Pipeline", value: salesCounts.inPipeline, bg: "#f0fdf4", border: "#bbf7d0", text: "#15803d" },
                      {
                        label: "Overdue", value: salesCounts.overdue, bg: "#fff5f5", border: "#fecaca", text: "#b91c1c",
                        filter: salesCounts.overdue > 0 ? { followup: "true", lastFollowupTo: todayStart.toISOString() } : null,
                        clickable: salesCounts.overdue > 0,
                      },
                      {
                        label: "Due Today", value: salesCounts.dueToday, bg: "#fffbeb", border: "#fde68a", text: "#92400e",
                        filter: salesCounts.dueToday > 0 ? { followup: "true", lastFollowupFrom: todayStart.toISOString(), lastFollowupTo: todayEnd.toISOString() } : null,
                        clickable: salesCounts.dueToday > 0,
                      },
                    ].map(({ label, value, bg, border, text, filter, clickable }) => (
                      <Tooltip key={label} title={clickable ? "Click to filter" : undefined}>
                        <div
                          onClick={() => {
                            if (!clickable || !filter) return;
                            dispatch(updateLeadList({ key: listKey, changes: { filters: filter, page: 1 } }));
                          }}
                          style={{
                            background: bg, border: `1px solid ${border}`, borderRadius: 10,
                            padding: "6px 14px", minWidth: 80, textAlign: "center",
                            cursor: clickable ? "pointer" : "default",
                            boxShadow: clickable ? "0 1px 4px rgba(0,0,0,0.07)" : "none",
                            transition: "box-shadow 0.15s",
                          }}
                        >
                          <div style={{ fontSize: 18, fontWeight: 700, color: text, lineHeight: 1.2 }}>{value}</div>
                          <div style={{ fontSize: 11, color: text, opacity: 0.8, marginTop: 1 }}>{label}</div>
                        </div>
                      </Tooltip>
                    ))}
                    <Tooltip title="Sales Leaderboard">
                      <div
                        onClick={() => setShowLeaderboard((p) => !p)}
                        style={{
                          background: showLeaderboard ? "#1677ff" : "#f0f9ff",
                          border: `1px solid ${showLeaderboard ? "#1677ff" : "#bae6fd"}`,
                          borderRadius: 10, padding: "6px 14px", textAlign: "center",
                          cursor: "pointer", minWidth: 80,
                          color: showLeaderboard ? "#fff" : "#0369a1",
                        }}
                      >
                        <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>
                          <i className="fa-solid fa-ranking-star" />
                        </div>
                        <div style={{ fontSize: 11, opacity: 0.8, marginTop: 1 }}>Leaderboard</div>
                      </div>
                    </Tooltip>
                    <Tooltip title="Find & Merge Duplicates">
                      <div
                        onClick={() => setShowDuplicates(true)}
                        style={{
                          background: "#fff7e6",
                          border: "1px solid #ffd591",
                          borderRadius: 10, padding: "6px 14px", textAlign: "center",
                          cursor: "pointer", minWidth: 80,
                          color: "#d46b08",
                        }}
                      >
                        <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>
                          <i className="fa-solid fa-code-merge" />
                        </div>
                        <div style={{ fontSize: 11, opacity: 0.8, marginTop: 1 }}>Duplicates</div>
                      </div>
                    </Tooltip>
                  </div>

                  {/* Duplicate Lead Merge Modal */}
                  <DuplicateLeadMerge
                    visible={showDuplicates}
                    onClose={() => setShowDuplicates(false)}
                    onMerged={() => dispatch(fetchLeads())}
                  />

                  {/* Sales Leaderboard */}
                  <SalesLeaderboard
                    visible={showLeaderboard}
                    onClose={() => setShowLeaderboard(false)}
                  />

                  {/* Bulk Actions Toolbar */}
                  {selectedRowKeys.length > 0 && (
                    <div style={{
                      display: "flex", alignItems: "center", gap: 10, marginBottom: 10,
                      padding: "8px 14px", background: "#f0f5ff", border: "1px solid #adc6ff",
                      borderRadius: 8, flexWrap: "wrap",
                    }}>
                      <span style={{ fontWeight: 600, fontSize: 13, color: "#1d39c4", whiteSpace: "nowrap" }}>
                        {selectedRowKeys.length} selected
                      </span>
                      <Select
                        placeholder="Status" size="small" allowClear
                        style={{ width: 120 }}
                        onChange={(v) => handleBulkUpdate("status", v)}
                        options={["New", "Active", "Nurturing", "Closed", "Lost", "On Hold"].map(s => ({ label: s, value: s }))}
                      />
                      <Select
                        placeholder="Stage" size="small" allowClear
                        style={{ width: 120 }}
                        onChange={(v) => handleBulkUpdate("stage", v)}
                        options={(leadFilters?.stage || []).filter(Boolean).map(s => ({ label: s, value: s }))}
                      />
                      <Select
                        placeholder="Priority" size="small" allowClear
                        style={{ width: 100 }}
                        onChange={(v) => handleBulkUpdate("priority", v)}
                        options={["Low", "Medium", "High"].map(s => ({ label: s, value: s }))}
                      />
                      <Select
                        placeholder="Assign To" size="small" allowClear showSearch
                        style={{ width: 140 }}
                        onChange={(v) => handleBulkUpdate("assignedTo", v)}
                        options={(leadFilters?.assignedTo || []).filter(Boolean).map(s => ({ label: s, value: s }))}
                      />
                      <Select
                        placeholder="Pipeline Stage" size="small" allowClear
                        style={{ width: 130 }}
                        onChange={(v) => { handleBulkUpdate("pipelineStage", v); }}
                        options={["Prospect", "Qualified", "Proposal", "Negotiation", "Onboarding", "Closed Won", "Closed Lost"].map(s => ({ label: s, value: s }))}
                      />
                      <button
                        onClick={() => handleBulkPipeline(true)}
                        style={{
                          padding: "2px 10px", fontSize: 12, borderRadius: 6,
                          border: "1px solid #15803d", background: "#dcfce7", color: "#15803d",
                          cursor: "pointer", fontWeight: 600,
                        }}
                      >
                        + Pipeline
                      </button>
                      <button
                        onClick={() => handleBulkPipeline(false)}
                        style={{
                          padding: "2px 10px", fontSize: 12, borderRadius: 6,
                          border: "1px solid #b91c1c", background: "#fee2e2", color: "#b91c1c",
                          cursor: "pointer", fontWeight: 600,
                        }}
                      >
                        - Pipeline
                      </button>
                      <button
                        onClick={() => setSelectedRowKeys([])}
                        style={{
                          padding: "2px 10px", fontSize: 12, borderRadius: 6,
                          border: "1px solid #d9d9d9", background: "#fff", color: "#595959",
                          cursor: "pointer", marginLeft: "auto",
                        }}
                      >
                        Clear
                      </button>
                    </div>
                  )}

                  <div className={`row ${prevVisibility ? "partial-width" : "full-width"}`}>
                    <div className="table-responsive-wrapper">
                      <CustomGridTable
                        className="table-striped"
                        rowKey="id"
                        columns={columns}
                        Id="sales-table"
                        dataSource={tableData?.length > 0 ? tableData : []}
                        scroll={{ x: 1400 }}
                        rowSelection={rowSelection}
                        pagination={{
                          current: page,
                          pageSize: 50,
                          total: pageCount || 0,
                          showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} items`,
                          onChange: handleTableChange,
                          showQuickJumper: true,
                          showSizeChanger: false,
                        }}
                        size="small"
                        onChange={handleTableChange}
                        rowClassName={getRowClassName}
                        onRow={(record) => ({
                          onClick: () => handleDetailPage(record?.id),
                        })}
                        locale={{
                          emptyText: <CustomNoRowsOverlay />,
                        }}
                      />
                    </div>
                  </div>


                </div>
              </div>
              <DeleteModal Name="Delete Ticket" />
            </>
          )}
        </>
      )}
    </>
  );
};

export default Sales;
